import { type FC } from "react";

import { PageReleaseForm } from "components/forms/PageReleaseForm";
import { relativeDayAndTimeFormat } from "i18n/helpers";
import { t } from "i18n/i18n";
import { Chip, ChipProps } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { Popover, PopoverButton, PopoverPanel } from "mds/components/Popover";
import { ChevronDownIcon, ClockIcon } from "mds/icons";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";

type PageReleaseButtonProps = {
  pageId: string;
};

const ChipButton: FC<Omit<ChipProps<"button">, "as">> = (props) => <Chip as="button" {...props} />;

export const PageReleaseButton: FC<PageReleaseButtonProps> = ({ pageId }) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["due_at", "released_at", "scheduled_release_at"],
  );
  const isReleased = Boolean(page?.released_at);
  const scheduledReleaseAt = page?.scheduled_release_at
    ? new Date(page.scheduled_release_at)
    : undefined;
  const dueAt = page?.due_at ? new Date(page.due_at) : undefined;

  if (isReleased) {
    return <Chip kind="gray">{t("page_release.released")}</Chip>;
  }

  const dateText = scheduledReleaseAt
    ? relativeDayAndTimeFormat(scheduledReleaseAt)
    : t("page_release.schedule");

  return (
    <Popover className="relative">
      <PopoverButton as={ChipButton} kind="white" title={dateText}>
        <IconText
          iconEnd={<ChevronDownIcon />}
          iconStart={scheduledReleaseAt ? <ClockIcon /> : undefined}
          text={dateText}
        />
      </PopoverButton>

      <PopoverPanel anchor="bottom" className="!z-[105]">
        {({ close }) => (
          <PageReleaseForm close={close} date={scheduledReleaseAt} dueAt={dueAt} pageId={pageId} />
        )}
      </PopoverPanel>
    </Popover>
  );
};
